import {
	dateToQuery,
	dateToUTCString,
	endOf,
	formatDate,
	parseDateFromQuery,
	startOf,
} from "@sembark-travel/datetime-utils"
import {
	AsyncSelect,
	Box,
	Button,
	ContentToggle,
	Divider,
	Dropdown,
	Icons,
	Inline,
	joinAttributes,
	Money,
	SelectProps,
	Stack,
	Table,
	Text,
	Time,
	useBreakpoints,
} from "@sembark-travel/ui-base"
import {
	DateRangePickerField,
	isTruthy,
	SelectField,
	SwitchInputField,
} from "@sembark-travel/ui-form"
import {
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { ButtonLink, Link, useLocationQuery } from "@sembark-travel/ui-router"
import { useEffect } from "react"
import { SelectTripOwners } from "../Trips"
import { IUser } from "../Users"
import { SelectWorldCountries, TWorldCountry } from "../World"
import { generatePath } from "../router-utils"
import { TVisa, TVisaServiceForm, TVisaType, XHR } from "./store"
import { useXHR } from "@sembark-travel/xhr"
import { TTripDestination } from "../TripDestinations"
import { TemporaryStorage } from "../TemporaryStorage"
import config from "../config"
import { PERMISSIONS, useCheckPermissions } from "../Auth"

type TFilters = TSearchParams & {
	countries?: Array<TWorldCountry>
	last_updated_by?: Array<IUser>
	rate_expire_after?: string | Date
	rate_expire_before?: string | Date
	only_deleted?: 1 | 0
}

type TFiltersInQuery = TSearchParams & {
	countries?: Array<string>
	last_updated_by?: Array<string>
	rate_expire_after?: string
	rate_expire_before?: string
	only_deleted?: 1 | 0
	include?: string
}

function filtersToQuery({
	countries,
	last_updated_by,
	rate_expire_after,
	rate_expire_before,
	only_deleted,
	...others
}: TFilters): TFiltersInQuery {
	const query: TFiltersInQuery = { ...others }

	if (last_updated_by) {
		query.last_updated_by = last_updated_by.map((c) => `${c.id}_${c.name}`)
	}
	if (rate_expire_after) {
		query.rate_expire_after = dateToQuery(rate_expire_after)
	}
	if (rate_expire_before) {
		query.rate_expire_before = dateToQuery(rate_expire_before)
	}
	if (countries) {
		query.countries = countries.map((c) => `${c.iso2}_${c.name}`)
	}
	if (only_deleted) {
		query.only_deleted = only_deleted
	}
	return query
}

function queryToFilters({
	countries,
	last_updated_by,
	rate_expire_after,
	rate_expire_before,
	only_deleted,
	...others
}: TFiltersInQuery): TFilters {
	const filters: TFilters = { ...others }
	if (rate_expire_after) {
		filters.rate_expire_after = parseDateFromQuery(rate_expire_after)
	}
	if (rate_expire_before) {
		filters.rate_expire_before = parseDateFromQuery(rate_expire_before)
	}
	if (countries?.length) {
		filters.countries = countries.map((d) => {
			const slices = d.split("_")
			return { iso2: slices[0], name: slices.slice(1).join("_") }
		}) as unknown as Array<TWorldCountry>
	}
	if (last_updated_by?.length) {
		filters.last_updated_by = last_updated_by.map((d) => {
			const slices = d.split("_")
			return { id: Number.parseInt(slices[0]), name: slices.slice(1).join("_") }
		}) as unknown as Array<IUser>
	}
	if (only_deleted && isTruthy(only_deleted)) {
		filters.only_deleted = 1
	}

	return filters
}

function filtersToAPIParams({
	rate_expire_after,
	rate_expire_before,
	last_updated_by,
	countries,
	only_deleted,
	...others
}: TFilters): TFiltersInQuery {
	const apiParams: TFiltersInQuery = { ...others }

	if (rate_expire_after) {
		apiParams.rate_expire_after = dateToUTCString(
			startOf(rate_expire_after, "day")
		)
	}
	if (rate_expire_before) {
		apiParams.rate_expire_before = dateToUTCString(
			endOf(rate_expire_before, "day")
		)
	}
	if (last_updated_by && Array.isArray(last_updated_by)) {
		apiParams.last_updated_by = last_updated_by.map((u) => `${u.id}`)
	}
	if (countries && Array.isArray(countries)) {
		apiParams.countries = countries.map((c) => `${c.iso2}`)
	}

	return apiParams
}

function Filters() {
	return (
		<Stack gap="4">
			<DateRangePickerField
				label="Rate Validity"
				fromName="rate_expire_after"
				toName="rate_expire_before"
				rangePresets={["Today", "Yesterday", "Next 7 Days", "Prev 7 Days"]}
			/>
			<SelectField
				select={SelectTripOwners}
				name="last_updated_by"
				label="Last Updated By"
				multiple
				fetchOnMount
			/>
			<SelectField
				select={SelectWorldCountries}
				name="countries"
				label="Country"
				multiple
				fetchOnMount
			/>
			<SwitchInputField name="only_deleted" label="Disabled Only" />
		</Stack>
	)
}

export function VisaServicesList() {
	const [query, setQuery] = useLocationQuery<TFilters, TFiltersInQuery>({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch<TFilters>(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const { hasPermission } = useCheckPermissions()
	const { xs, sm, md } = useBreakpoints()
	const isSmallViewport = xs || sm || md
	return (
		<Search
			initialParams={params}
			onSearch={(params) => {
				setParams({ ...params, page: 1 })
			}}
			title="Visa"
			actions={
				<Inline gap="2" justifyContent="end">
					{hasPermission(PERMISSIONS.CREATE_VISA_SERVICES) ? (
						<ButtonLink to={generatePath("/visa/new")}>New Visa</ButtonLink>
					) : null}
					<Dropdown alignRight>
						<Dropdown.ToggleButton size="sm" level="tertiary">
							<Icons.DotsVertical />
						</Dropdown.ToggleButton>
						<Dropdown.Menu>
							<Dropdown.MenuItem>
								<DownloadVisaDetails
									params={{
										...filtersToAPIParams(params),
										include: params.only_deleted ? "only_deleted" : undefined,
									}}
								/>
							</Dropdown.MenuItem>
						</Dropdown.Menu>
					</Dropdown>
				</Inline>
			}
			Filters={Filters}
			placeholder="Search by visa name, or visa type…"
		>
			<ListView<TVisa, TFilters>
				pageKey="/visa-services"
				fetch={(xhr, params) => {
					return XHR(xhr).getVisaServices({
						...filtersToAPIParams(params),
						include: [
							isTruthy(params.only_deleted) ? "only_deleted" : null,
							"updated_by",
							"created_by",
						]
							.filter(Boolean)
							.join(","),
					})
				}}
				params={params}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items: visaServices }) => {
					return isSmallViewport ? (
						<VisaCardView visaServices={visaServices} />
					) : (
						<VisaTableView visaServices={visaServices} />
					)
				}}
			</ListView>
		</Search>
	)
}

function VisaTableView({ visaServices }: { visaServices: TVisa[] }) {
	return (
		<Table
			responsive
			bordered
			hover
			headers={[
				"",
				<Box style={{ maxWidth: "150px" }}>Country</Box>,
				<Box style={{ maxWidth: "150px" }}>Visa</Box>,
				<Inline alignItems="end" justifyContent="between">
					<Box style={{ width: "40%" }} paddingX="1">
						Visa Types
					</Box>
					<Box
						wordBreak="words"
						whiteSpace="normal"
						style={{ width: "30%" }}
						minWidth="max"
						textAlign="left"
						paddingX="1"
					>
						Rate & Validity
					</Box>
					<Box
						wordBreak="words"
						whiteSpace="normal"
						style={{ width: "30%" }}
						textAlign="left"
						minWidth="max"
						paddingX="1"
					>
						Last Updated
					</Box>
				</Inline>,
			]}
			rows={visaServices.map(
				({ id, countries, name, visa_types, currency }) => [
					<Box key={name}>
						<Icons.Visa size="7" />
					</Box>,
					<Text key={name}>
						{countries?.map((c) => c.name).join(", ") ?? "-"}
					</Text>,
					<Link
						to={generatePath("/visa/:visaServiceId", {
							visaServiceId: String(id),
						})}
						color="accent"
						fontWeight="semibold"
						cursor="pointer"
						key={name}
					>
						{name}
					</Link>,
					<VisaTypeList visaTypes={visa_types} currency={currency} />,
				]
			)}
		></Table>
	)
}

function VisaCardView({ visaServices }: { visaServices: TVisa[] }) {
	return (
		<Box as="ol">
			{visaServices.map(({ id, countries, name, visa_types, currency }) => (
				<Box
					as="li"
					key={id}
					padding="4"
					borderWidth="1"
					rounded="lg"
					marginBottom="4"
					bgColor="subtle"
				>
					<Box marginBottom="4">
						<Link
							fontSize="md"
							to={generatePath("/visa/:visaServiceId", {
								visaServiceId: String(id),
							})}
							color="accent"
							anchored
							fontWeight="semibold"
						>
							<Inline gap="3" alignItems="center">
								<Box>
									<Icons.Visa size="7" />
								</Box>
								<Stack gap="1">
									<Box>{name}</Box>
									{countries?.length ? (
										<Text color="muted" fontSize="sm">
											{countries.map((c) => c.name).join(", ")}
										</Text>
									) : null}
								</Stack>
							</Inline>
						</Link>
					</Box>
					<ContentToggle
						renderItem={(s: TVisaType) => (
							<>
								<Inline alignItems="center" gap="1" marginBottom="1">
									<Text textAlign="left" fontWeight="semibold" fontSize="md">
										{s.name}
									</Text>
									{s.deleted_at ? (
										<Box>
											<Icons.Ban color="danger" />
										</Box>
									) : null}
								</Inline>
								<Inline flexWrap="wrap">
									{joinAttributes(
										s.cost_price && currency ? (
											<Box>
												<Money money={s.cost_price} showCurrency />
											</Box>
										) : null,
										s.price_valid_till ? (
											<Box>
												<Text textAlign="left">
													rate expiry on :{" "}
													{formatDate(s.price_valid_till, "DD MMM, YYYY")}
												</Text>
											</Box>
										) : null,
										<VisaTypeUpdatedInfo visaType={s} cardView />
									)}
								</Inline>
								<Divider sm />
							</>
						)}
						data={visa_types || []}
					/>
				</Box>
			))}
		</Box>
	)
}

function VisaTypeUpdatedInfo({
	visaType,
	cardView,
}: {
	visaType: TVisaType
	cardView?: boolean
}) {
	const { created_at, created_by, updated_at, updated_by } = visaType
	let lastChange: { user: IUser; date: string } | null = null
	if (updated_by && updated_at) {
		lastChange = { user: updated_by, date: updated_at }
	} else if (created_by && created_at) {
		lastChange = { user: created_by, date: created_at }
	}
	if (!lastChange) return null

	if (cardView) {
		return joinAttributes(
			<Box>
				<Text wordBreak="words" whiteSpace="normal" color="muted">
					last updated by: {lastChange.user.name}
				</Text>
			</Box>,
			<Box>
				<Text>
					last updated on: {formatDate(lastChange.date, "DD MMM, YYYY")}
				</Text>
			</Box>
		)
	}

	return (
		<Box
			wordBreak="words"
			whiteSpace="normal"
			style={{
				padding: "8px",
				width: "30%",
				maxWidth: "30%",
			}}
			minWidth="max"
			borderBottomColor="emphasis"
		>
			<Inline>
				<Time
					timestamp={lastChange.date ?? undefined}
					localTimestamp={lastChange.date ?? undefined}
					format="DD MMM, YYYY"
					wordBreak="words"
					whiteSpace="normal"
					textAlign={"right"}
				/>
			</Inline>
			<Inline>
				<Box>
					<Icons.User />
				</Box>
				<Text
					wordBreak="words"
					whiteSpace="normal"
					color={"muted"}
					textAlign={"left"}
				>
					{lastChange.user.name}
				</Text>
			</Inline>
		</Box>
	)
}

function VisaTypeList({
	visaTypes,
	currency,
}: {
	visaTypes: TVisa["visa_types"]
	currency?: string
}) {
	return (
		<ContentToggle
			gap={"0"}
			renderItem={(s: TVisa["visa_types"][number]) => (
				<Inline
					key={s.name}
					alignItems="stretch"
					borderBottomWidth="1"
					borderBottomColor="emphasis"
				>
					<Box
						style={{
							width: "40%",
							maxWidth: "40%",
							padding: "8px",
						}}
						borderBottomColor="emphasis"
					>
						<Inline alignItems={"center"} gap={"1"}>
							<Text>{s.name}</Text>
							{s.deleted_at ? (
								<Box>
									<Icons.Ban color={"danger"} />
								</Box>
							) : null}
						</Inline>
					</Box>
					<Box
						textAlign="center"
						wordBreak="words"
						whiteSpace="normal"
						style={{
							padding: "8px",
							width: "30%",
							maxWidth: "30%",
						}}
						minWidth="max"
						borderBottomColor="emphasis"
					>
						<Stack>
							{s.cost_price && currency ? (
								<Money money={s.cost_price} showCurrency textAlign="left" />
							) : null}
							{s.price_valid_till ? (
								<Text textAlign="left">
									{formatDate(s.price_valid_till, "DD MMM, YYYY")}
								</Text>
							) : null}
						</Stack>
					</Box>

					<VisaTypeUpdatedInfo visaType={s} />
				</Inline>
			)}
			data={visaTypes}
		/>
	)
}

function DownloadVisaDetails({ params }: { params: TFiltersInQuery }) {
	return (
		<TemporaryStorage
			initUrl="/visa-services/download"
			initParams={{
				...params,
				timezone_offset: config.timezoneOffset,
			}}
		>
			{({ message, generate, url, reset, isGenerating }) =>
				url ? (
					<Button
						as="a"
						href={url}
						target="_blank"
						download
						level="secondary"
						onClick={() => reset()}
					>
						{message}
					</Button>
				) : (
					<Button
						onClick={generate}
						disabled={isGenerating}
						level="secondary"
						size="sm"
					>
						{message || (
							<>
								<Icons.DocumentDownload /> Download Visa Details
							</>
						)}
					</Button>
				)
			}
		</TemporaryStorage>
	)
}

export function SelectVisaServices({
	country,
	tripDestinations,
	fieldName,
	startDate,
	...props
}: Readonly<
	Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
		country?: TWorldCountry
		tripDestinations: Array<TTripDestination>
		fieldName?: string
		startDate: string
	}
>) {
	const xhr = useXHR()

	return (
		<AsyncSelect
			key={country?.id}
			multiple
			{...props}
			fetch={(q) =>
				XHR(xhr).getVisaServicesForSelect({
					q,
					countries: country ? [country]?.map((c) => c.iso2) : null,
					trip_destinations: tripDestinations.map((td) => td.id),
					include: "with_documents",
				})
			}
		/>
	)
}

export function SelectVisaTypes({
	country,
	tripDestinations,
	fieldName,
	startDate,
	visa,
	...props
}: Readonly<
	Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
		country?: TWorldCountry
		tripDestinations?: Array<TTripDestination>
		fieldName?: string
		visa?: TVisaServiceForm | TVisa
		startDate?: string
	}
>) {
	const xhr = useXHR()

	return (
		<AsyncSelect
			key={visa?.id}
			multiple
			{...props}
			optionRenderer={({ option }: { option: TVisaType }) => {
				return (
					<Box>
						<Inline alignItems="center" gap="2">
							<Box>{option?.name}</Box>
							<Box>
								{option.deleted_at ? <Icons.Ban color="warning" /> : null}
							</Box>
						</Inline>
					</Box>
				)
			}}
			fetch={(q) =>
				XHR(xhr)
					.showVisaServices(visa?.id, {
						q,
						include: "without_deleted_visa_types",
					})
					.then((resp) => {
						return resp.visa_types
					})
			}
		/>
	)
}

export function SelectVisaTypeServices(props: Readonly<SelectProps>) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			multiple
			{...props}
			fetch={(q) => XHR(xhr).getVisaServicesForSelect({ q })}
		/>
	)
}
